/**
 * Lesson Flow Page — /lessons/book (BUT-843).
 *
 * Mirrors `DemoFlowPage`'s shell (same `StepProgress`, same
 * `BookingStepManager`). The header is the shared `LessonPageHeader`
 * (design round, BUT-843) rather than a `text-3xl` heading of its own, matching
 * the Figma sizing used across /lessons. Unlike the demo flow, the three
 * logical steps here ARE the three visual steps, and `step` comes straight
 * from `useLessonFlow()` (`LessonFlowContext`) rather than local state — the
 * flow's selection already lives there, seeded from the URL.
 *
 * Sign-in gate: `POST /lesson-bookings` is `scopeToAuthenticatedUser`, but
 * the instructor picker (step 0) is browsable by a guest
 * (`listClubInstructors` is `optionalAuth`). So the gate sits here, at the
 * page shell, swapping step 2's content for a "Sign in to book" prompt
 * rather than inside `LessonPaymentStep` itself — `resolveLessonCta` takes
 * no `isAuthenticated` input by design.
 *
 * The page owns `PaymentProvider` (like `BookingFlowPage`): step 2's Square card
 * form calls `usePayment`, which throws outside it.
 *
 * @module pages/lessons/LessonFlowPage
 */

import { CoachIcon, CalendarIcon, CheckoutIcon, ProfileIcon } from '@butter/ui/icons';
import { EmptyState } from '@butter/ui/components/feedback';
import { StepProgress, type Step } from '~/ui/navigation/StepProgress';
import { BookingStepManager } from '~/pages/booking/components/BookingStepManager';
import { PaymentProvider } from '~/features/payment';
import { useAuth } from '~/services/auth';
import { useHashModal } from '~/hooks/useHashModal';
import { useLessonFlow, useUserLessons } from '~/services/internalApi/lessons';
import { resolveActivePack, resolveLessonCoverage } from '@butter/library/lessons';
import { LessonPageHeader } from './components';
import { LessonInstructorStep } from './steps/LessonInstructorStep';
import { LessonTimeStep } from './steps/LessonTimeStep';
import { LessonPaymentStep } from './steps/LessonPaymentStep';

const lessonSteps: Step[] = [
  { id: 'instructor', label: 'Instructor', icon: <CoachIcon className="size-5" /> },
  { id: 'lesson-time', label: 'Lesson Time', icon: <CalendarIcon className="size-5" /> },
  { id: 'confirmation', label: 'Confirmation', icon: <CheckoutIcon className="size-5" /> },
];

const pageTitles = ['Book a Lesson', 'Book a Lesson', 'Payment Details'];
const pageSubtitles = [
  "Let's get you set up for your lesson",
  "Let's get you set up for your lesson",
  'Review your lesson details and complete payment',
];

/** Step 2's content for a guest — `useHashModal` opens the same global sign-in modal `MainLayout` renders. */
function LessonSignInGate() {
  const signInModal = useHashModal('dialog-sign-in');
  return (
    <div className="rounded-3xl bg-bg-secondary">
      <EmptyState
        variant="muted"
        icon={<ProfileIcon className="size-7 text-fg-muted" />}
        title="Sign in to book your lesson"
        description="Sign in to review your lesson details and complete payment."
        action={
          <button
            type="button"
            onClick={signInModal.open}
            className="rounded-full bg-theme-600 px-6 py-3 text-sm font-semibold text-white text-interactive:bg-theme-700"
            data-testid="lesson-signin-required-btn"
          >
            Sign in to book
          </button>
        }
        data-testid="lesson-signin-required"
      />
    </div>
  );
}

export function LessonFlowPage() {
  const { isAuthenticated } = useAuth();
  const { step, durationMinutes } = useLessonFlow();

  // Cache hit on the same query key `LessonPaymentStep` uses.
  const { userLessons } = useUserLessons({ enabled: isAuthenticated });
  const activePack = resolveActivePack(userLessons);
  const isFullyCovered = resolveLessonCoverage(activePack, durationMinutes ?? 0).kind === 'full';

  const isConfirmOnly = step === 2 && isAuthenticated && isFullyCovered;
  const pageTitle = isConfirmOnly ? 'Confirm your lesson' : pageTitles[step];
  const pageSubtitle = isConfirmOnly
    ? 'Review your lesson details and confirm your booking'
    : pageSubtitles[step];

  return (
    <PaymentProvider>
      <LessonPageHeader title={pageTitle} subtitle={pageSubtitle} />

      <StepProgress steps={lessonSteps} currentStep={step} />

      <BookingStepManager currentStep={step}>
        <LessonInstructorStep />
        <LessonTimeStep />
        {isAuthenticated ? <LessonPaymentStep /> : <LessonSignInGate />}
      </BookingStepManager>
    </PaymentProvider>
  );
}
